import { orgSelectors } from 'ducks/orgs'
import { isEqual } from 'lodash'
import { ExpoState } from 'opensolar-pageeditor'
import { getUserType, UserType } from 'opensolar-pageeditor/src/renderer/userUtils'
import { CountryMapType } from 'pages/mfa/types'
import { useSetLocale } from 'ra-core'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import appStorage from 'storage/appStorage'
import { useFeatureFlag } from 'util/featureFlags'
import { useCountriesWithStands } from './hooks/useCountriesWithStands'
import { useGetProposal, useUserDefaults } from './hooks/useUserDefaults'
import {
  asPathState,
  getExpoPathForState,
  normaliseExpoState,
  persistExpoPreferencesToLocalStorage,
  resolveExpoState,
} from './utils'

interface ExpoUserContextType {
  selectedCountry?: string | null
  selectedLocale?: string | null
  isCountryOverrideEnabled: boolean
  brandSlug?: string | null
  customerLatestProposalCountry: string | null
  updateExpoState: (data: Partial<ExpoState>) => void
  availableCountries: CountryMapType[] | null
}

export const ExpoContext = createContext<ExpoUserContextType | null>(null)

export const ExpoContextProvider = ({ children }) => {
  const history = useHistory()
  const availableCountries = useCountriesWithStands()
  const expoEnabled = useFeatureFlag('expo', 'on')
  const setLocale = useSetLocale()

  const { getDefaultCountry: getPreferredCountry, getDefaultLocale: getPreferredLocale } = useUserDefaults()
  const preferredLocale = getPreferredLocale()

  // FIXME this is likely to cause unnecessary re-renders when this loads.
  const [customerLatestProposalCountry, setCustomerLatestProposalCountry] = useState<string | null>(null)

  const { getLatestProject, getProposal } = useGetProposal()
  const org = useSelector(orgSelectors.getOrg)
  const userType: UserType = getUserType(org)

  useEffect(() => {
    const getLatestProposalCountry = async () => {
      if (userType !== UserType.CUSTOMER) return

      const project = await getLatestProject()
      const projectId = project?.[0]?.id

      if (!projectId) return

      const latestProposal = await getProposal(projectId)
      const country = latestProposal?.data?.[0]?.projects?.[0]?.country_iso2
      setCustomerLatestProposalCountry(country)
    }
    getLatestProposalCountry()
  }, [])

  //expoState - text values should always be normalised to lowercase
  const [expoState, setExpoState] = useState<ExpoState>({
    selectedCountry: null,
    selectedLocale: null,
    isCountryOverrideEnabled: false,
    brandSlug: undefined,
  })

  const updateStateAndRedirect = (updatedExpoState: ExpoState, currentExpoState: ExpoState, windowPath: string) => {
    setExpoState(updatedExpoState)
    persistExpoPreferencesToLocalStorage(updatedExpoState)

    const updatedPath = getExpoPathForState(updatedExpoState)
    if (updatedPath !== windowPath) {
      if (isEqual(asPathState(updatedExpoState), asPathState(currentExpoState))) {
        history.replace(updatedPath)
      } else {
        history.push(updatedPath)
      }
    }
  }

  const handlePathChange = useCallback(
    (windowPath: string) => {
      if (availableCountries === null) return

      const preferredCountry = getPreferredCountry(customerLatestProposalCountry)
      const updatedExpoState = resolveExpoState(preferredCountry, preferredLocale, availableCountries, windowPath)

      updateStateAndRedirect(updatedExpoState, expoState, windowPath)
    },
    [availableCountries, customerLatestProposalCountry, preferredLocale]
  )

  useEffect(() => {
    const pathName = window.location.pathname
    if (!pathName.startsWith('/expo') || !expoEnabled) {
      return
    }

    handlePathChange(pathName)
  }, [window.location.pathname, expoEnabled, handlePathChange, availableCountries])

  const updateExpoStateInternal = useCallback(
    (updates: Partial<ExpoState> = {}) => {
      const normalisedUpdates = normaliseExpoState(updates)
      const updatedExpoState = { ...expoState, ...normalisedUpdates }

      if (normalisedUpdates.selectedLocale) {
        setLocale(normalisedUpdates.selectedLocale)
        appStorage.setLocale(normalisedUpdates.selectedLocale)
      }

      updateStateAndRedirect(updatedExpoState, expoState, window.location.pathname)
    },
    [expoState, history, setLocale]
  )

  const updateExpoStateInternalRef = useRef(updateExpoStateInternal)
  updateExpoStateInternalRef.current = updateExpoStateInternal

  // Stable function that consumers can safely depend on
  const updateExpoState = useCallback((updates: Partial<ExpoState> = {}) => {
    updateExpoStateInternalRef.current(updates)
  }, [])

  const value: ExpoUserContextType = {
    selectedCountry: expoState.selectedCountry,
    selectedLocale: expoState.selectedLocale,
    brandSlug: expoState.brandSlug,
    isCountryOverrideEnabled: expoState.isCountryOverrideEnabled,
    updateExpoState: updateExpoState,
    customerLatestProposalCountry,
    availableCountries: availableCountries,
  }
  return <ExpoContext.Provider value={value}>{children}</ExpoContext.Provider>
}
